import * as React from 'react';
import { useCallback, useState, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { DataLake } from '@graylog/enterprise-api';

import { MenuItem } from 'components/bootstrap';
import BulkActionsDropdown from 'components/common/EntityDataTable/BulkActionsDropdown';
import BulkRetrievalModal from 'data-lake/preview/BulkRetrievalModal';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import useSelectedEntities from 'components/common/EntityDataTable/hooks/useSelectedEntities';
import AddEvidenceModal from 'security-app/components/Investigations/plugin/shared/AddEvidenceModal';
import { useInvestigationDrawer } from 'security-app/components/Investigations/contexts/InvestigationDrawer';
import { useGetInvestigationDetails } from 'security-app/hooks/useInvestigationsAPI';
import SelectableEntitiesContext from 'logview/components/contexts/SelectableLogViewMessagesContext';
import { ConfirmDialog } from 'components/common';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import type { BulkAddEvidenceAPIType } from 'security-app/hooks/api/investigationsAPI.types';
import useRetrieval from 'data-lake/preview/hooks/useRetrieval';
import AddToInvestigationHelp from 'data-lake/preview/AddToInvestigationHelp';
import useActionCondition from 'security-app/components/Investigations/plugin/useActionCondition';

type Props<BulkRetrievalPayload> = {
  retrievalPayload: BulkRetrievalPayload;
};

const BulkActions = <BulkRetrievalPayload extends unknown>({ retrievalPayload }: Props<BulkRetrievalPayload>) => {
  const [showRetrievalModal, setShowRetrievalModal] = useState(false);
  const sendTelemetry = useSendTelemetry();
  const { selectedEntities } = useSelectedEntities();
  const { selectableLogViewMessages } = useContext(SelectableEntitiesContext);
  const selectedMessages = selectableLogViewMessages.filter(({ id }) => selectedEntities.includes(id));
  const addEvidenceModalRef = React.useRef(null);
  const queryClient = useQueryClient();
  const { selectedInvestigationId, setCollapsed } = useInvestigationDrawer();
  const { currentInvestigation } = useGetInvestigationDetails(selectedInvestigationId);
  const [addingToInvestigation, setAddingToInvestigation] = useState(false);
  const [showAddToCurrentModal, setShowAddToCurrentModal] = useState(false);
  const { bulkRetrievePayload } = useRetrieval();
  const { selectedDataLake } = useSelectedDataLake();

  const displayInvestigationActions = useActionCondition();

  const toggleShowModal = useCallback(() => {
    if (!showRetrievalModal) {
      sendTelemetry(TELEMETRY_EVENT_TYPE.DATALAKE.PREVIEW[selectedDataLake.type].RETRIEVE_LOGS_BULK_ACTION_CLICK, {
        app_pathname: '/data-storage/preview',
        app_section: 'log-view-widget',
        event_details: {
          messages_amount: selectedEntities.length,
          data_lake_storage_type: selectedDataLake.storageType,
        },
      });
    }
    setShowRetrievalModal((cur) => !cur);
  }, [selectedDataLake.storageType, selectedDataLake.type, selectedEntities.length, sendTelemetry, showRetrievalModal]);

  const disabledAdd = !currentInvestigation || currentInvestigation?.archived;

  const updateInvestigation = useCallback(
    (investigationIds: Array<string>) =>
      DataLake.addMultipleMessagesToInvestigation({
        investigation_ids: investigationIds,
        retrieval_request: bulkRetrievePayload(
          selectedMessages.map(({ id, timestamp }) => ({ id, timestamp })),
          selectedDataLake.id,
          retrievalPayload,
        ),
      }).then(async () => {
        await new Promise((resolve: (arg?: any) => void) => {
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ['get-investigation-details'] });

            resolve();
          }, 1000);
        });
      }),
    [bulkRetrievePayload, queryClient, retrievalPayload, selectedDataLake.id, selectedMessages],
  );

  const onAddLog = async () => {
    setAddingToInvestigation(true);
    if (disabledAdd) return;

    await updateInvestigation([selectedInvestigationId]);

    setAddingToInvestigation(false);
    setShowAddToCurrentModal(false);
    setCollapsed(false);
  };

  const onAddEvidence = async (payload: BulkAddEvidenceAPIType) => updateInvestigation(payload.investigation_ids);

  return (
    <>
      <BulkActionsDropdown bsSize="xs">
        <MenuItem onSelect={toggleShowModal}>Retrieve Logs</MenuItem>
        {displayInvestigationActions && (
          <>
            {disabledAdd || (
              <MenuItem onClick={() => setShowAddToCurrentModal(true)} icon="extension">
                Add to investigation
              </MenuItem>
            )}
            <MenuItem onClick={() => addEvidenceModalRef.current.toggle()} icon="extension">
              Select an investigation
            </MenuItem>
          </>
        )}
      </BulkActionsDropdown>
      {showRetrievalModal && (
        <BulkRetrievalModal<BulkRetrievalPayload> onCancel={toggleShowModal} retrievalPayload={retrievalPayload} />
      )}
      {showAddToCurrentModal && (
        <ConfirmDialog
          show
          isAsyncSubmit
          isSubmitting={addingToInvestigation}
          btnConfirmText="Add to investigation"
          onConfirm={onAddLog}
          submitLoadingText="Adding to investigation..."
          onCancel={() => setShowAddToCurrentModal(false)}
          title="Adding messages to current investigation">
          <AddToInvestigationHelp />
        </ConfirmDialog>
      )}
      {displayInvestigationActions && (
        <AddEvidenceModal
          index={undefined}
          ids={selectedEntities}
          type="logs"
          ref={addEvidenceModalRef}
          onSubmit={onAddEvidence}>
          <AddToInvestigationHelp />
        </AddEvidenceModal>
      )}
    </>
  );
};

export default BulkActions;
